import Icon from 'components/Icon';
import { styleConstants } from 'theme/styleConstants';
import { styled, SxProps, Theme } from '@mui/material/styles';
import Tooltip from 'components/Tooltip';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { openModal } from 'features/modal/modalSlice';
import { ROOM_SETTINGS_CAPTIONS_TAB_INDEX } from 'features/transcripts/constants';
import {
  selectCaptionsSettingsAvailable,
  selectCaptionsSpokenLanguage,
  selectSpokenLanguageOverridden,
} from 'features/transcripts/slices/captions/captionsSlice';
import { getCaptionsLanguageLabel } from 'features/transcripts/utils/getCaptionsLanguageLabel';

const badgeStyles = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: styleConstants.featureBadge.width,
  height: styleConstants.featureBadge.height,
  borderRadius: '8px',
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
});

const Root = styled('div')(({ theme }) => badgeStyles(theme));

const InteractiveRoot = styled('button')(({ theme }) => ({
  ...badgeStyles(theme),
  border: 0,
  padding: 0,
  cursor: 'pointer',
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.common.white}`,
    outlineOffset: 2,
  },
}));

type TranscriptionBadgeProps = {
  sx?: SxProps;
};

const TranscriptionBadge = ({ sx }: TranscriptionBadgeProps) => {
  const { t } = useTranslation('room');
  const dispatch = useAppDispatch();

  const spokenLanguage = useAppSelector(selectCaptionsSpokenLanguage);
  const captionsSettingsAvailable = useAppSelector(selectCaptionsSettingsAvailable);
  const spokenLanguageOverridden = useAppSelector(selectSpokenLanguageOverridden);

  const tooltip = t(
    spokenLanguageOverridden
      ? 'topbar.transcripts_badge_tooltip_personal'
      : 'topbar.transcripts_badge_tooltip',
    { language: getCaptionsLanguageLabel(spokenLanguage) }
  );

  const icon = <Icon name="microphone-text-2" sx={{ fontSize: 32 }} />;

  const openCaptionsSettings = () => {
    dispatch(openModal('roomSettings', { activeTab: ROOM_SETTINGS_CAPTIONS_TAB_INDEX }));
  };

  if (!captionsSettingsAvailable) {
    return (
      <Tooltip title={tooltip} placement="bottom">
        <Root sx={sx} data-testid="transcription-badge">
          {icon}
        </Root>
      </Tooltip>
    );
  }

  return (
    // The tooltip states what is happening, so it describes the button instead of naming it.
    <Tooltip title={tooltip} placement="bottom" describeChild>
      <InteractiveRoot
        sx={sx}
        type="button"
        aria-label={t('topbar.transcripts_badge_aria_label')}
        onClick={openCaptionsSettings}
        data-testid="transcription-badge"
      >
        {icon}
      </InteractiveRoot>
    </Tooltip>
  );
};

export default TranscriptionBadge;
